/* ============================================
   UTILITY HELPER FUNCTIONS
   The Creative Orchestra - GEMINI Framework
   ============================================ */

import { STORAGE_KEYS, BREAKPOINTS } from './constants';

/* ============================================
   STRING UTILITIES
   ============================================ */

/**
 * Capitalize the first letter of a string
 * @param {string} str - Input string
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Convert string to title case
 * @param {string} str - Input string
 * @returns {string} Title cased string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generate a slug from a string
 * @param {string} str - Input string
 * @returns {string} Slugified string
 */
export const slugify = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
};

/* ============================================
   NUMBER UTILITIES
   ============================================ */

/**
 * Clamp a number between min and max values
 * @param {number} num - Number to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped number
 */
export const clamp = (num, min, max) => {
  return Math.min(Math.max(num, min), max);
};

/**
 * Generate a random number between min and max
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Random number
 */
export const randomBetween = (min, max) => {
  return Math.random() * (max - min) + min;
};

/**
 * Generate a random integer between min and max (inclusive)
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Random integer
 */
export const randomIntBetween = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Format a number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Calculate percentage
 * @param {number} value - Current value
 * @param {number} total - Total value
 * @returns {number} Percentage (0-100)
 */
export const calculatePercentage = (value, total) => {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
};

/* ============================================
   ARRAY UTILITIES
   ============================================ */

/**
 * Shuffle an array using Fisher-Yates algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled array
 */
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Get unique values from an array
 * @param {Array} array - Input array
 * @returns {Array} Array with unique values
 */
export const uniqueArray = (array) => {
  return [...new Set(array)];
};

/**
 * Group array items by a key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {Object} Grouped object
 */
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) result[group] = [];
    result[group].push(item);
    return result;
  }, {});
};

/* ============================================
   DOM & SCROLL UTILITIES
   ============================================ */

/**
 * Smooth scroll to element
 * @param {string} elementId - Element ID to scroll to
 * @param {number} offset - Offset from top (default: 80 for nav)
 */
export const scrollToElement = (elementId, offset = 80) => {
  const element = document.getElementById(elementId);
  if (element) {
    const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

/**
 * Scroll to top of page
 * @param {boolean} smooth - Use smooth scrolling
 */
export const scrollToTop = (smooth = true) => {
  window.scrollTo({
    top: 0,
    behavior: smooth ? 'smooth' : 'auto',
  });
};

/**
 * Get current scroll percentage
 * @returns {number} Scroll percentage (0-100)
 */
export const getScrollPercentage = () => {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (docHeight === 0) return 0;
  return Math.round((scrollTop / docHeight) * 100);
};

/**
 * Check if element is in viewport
 * @param {HTMLElement} element - Element to check
 * @param {number} threshold - Visibility threshold (0-1)
 * @returns {boolean} Is in viewport
 */
export const isInViewport = (element, threshold = 0) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  
  const vertInView = rect.top <= windowHeight * (1 - threshold) && rect.bottom >= windowHeight * threshold;
  const horInView = rect.left <= windowWidth * (1 - threshold) && rect.right >= windowWidth * threshold;
  
  return vertInView && horInView;
};

/* ============================================
   LOCAL STORAGE UTILITIES
   ============================================ */

/**
 * Get item from localStorage with JSON parsing
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Parsed value or default
 */
export const getStorageItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Error reading from localStorage: ${key}`, error);
    return defaultValue;
  }
};

/**
 * Set item in localStorage with JSON stringification
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 */
export const setStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Error writing to localStorage: ${key}`, error);
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 */
export const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Error removing from localStorage: ${key}`, error);
  }
};

/**
 * Clear all GEMINI-related items from localStorage
 */
export const clearAllStorage = () => {
  Object.values(STORAGE_KEYS).forEach((key) => {
    removeStorageItem(key);
  });
};

/* ============================================
   DATE & TIME UTILITIES
   ============================================ */

/**
 * Format reading time estimate
 * @param {string} content - Content to estimate
 * @param {number} wordsPerMinute - Reading speed (default: 200)
 * @returns {string} Formatted reading time
 */
export const getReadingTime = (content, wordsPerMinute = 200) => {
  if (!content) return '1 min read';
  const wordCount = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
};

/**
 * Format date to readable string
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/* ============================================
   RESPONSIVE UTILITIES
   ============================================ */

/**
 * Get current breakpoint name
 * @returns {string} Breakpoint name
 */
export const getCurrentBreakpoint = () => {
  const width = window.innerWidth;
  if (width < BREAKPOINTS.sm) return 'xs';
  if (width < BREAKPOINTS.md) return 'sm';
  if (width < BREAKPOINTS.lg) return 'md';
  if (width < BREAKPOINTS.xl) return 'lg';
  if (width < BREAKPOINTS['2xl']) return 'xl';
  return '2xl';
};

/**
 * Check if current viewport is mobile
 * @returns {boolean} Is mobile
 */
export const isMobile = () => {
  return window.innerWidth < BREAKPOINTS.md;
};

/**
 * Check if current viewport is tablet
 * @returns {boolean} Is tablet
 */
export const isTablet = () => {
  return window.innerWidth >= BREAKPOINTS.md && window.innerWidth < BREAKPOINTS.lg;
};

/**
 * Check if current viewport is desktop
 * @returns {boolean} Is desktop
 */
export const isDesktop = () => {
  return window.innerWidth >= BREAKPOINTS.lg;
};

/* ============================================
   ANIMATION UTILITIES
   ============================================ */

/**
 * Calculate orbital position for an orb
 * @param {number} index - Orb index (0-7)
 * @param {number} total - Total number of orbs
 * @param {number} radius - Orbit radius
 * @param {number} offsetAngle - Starting angle offset (radians)
 * @returns {Object} { x, y, angle } position
 */
export const calculateOrbitalPosition = (index, total, radius, offsetAngle = 0) => {
  const angle = (2 * Math.PI * index) / total + offsetAngle;
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    angle: angle * (180 / Math.PI), // Convert to degrees
  };
};

/**
 * Generate random star positions for constellation
 * @param {number} count - Number of stars
 * @param {number} width - Container width
 * @param {number} height - Container height
 * @returns {Array} Array of star positions
 */
export const generateStarPositions = (count, width, height) => {
  return Array.from({ length: count }, () => ({
    x: randomBetween(0, width),
    y: randomBetween(0, height),
    size: randomBetween(1, 3),
    opacity: randomBetween(0.3, 1),
    twinkleDelay: randomBetween(0, 5),
    twinkleDuration: randomBetween(2, 5),
  }));
};

/**
 * Generate spectrum bar heights
 * @param {number} count - Number of bars
 * @returns {Array} Array of heights (0-100)
 */
export const generateSpectrumHeights = (count) => {
  return Array.from({ length: count }, () => randomBetween(20, 100));
};

/* ============================================
   CHAPTER UTILITIES
   ============================================ */

/**
 * Parse chapter ID to get chapter number
 * @param {string} chapterId - Chapter ID (e.g., '01', '02')
 * @returns {number} Chapter number
 */
export const getChapterNumber = (chapterId) => {
  return parseInt(chapterId, 10);
};

/**
 * Format chapter ID with leading zero
 * @param {number} num - Chapter number
 * @returns {string} Formatted ID (e.g., '01', '02')
 */
export const formatChapterId = (num) => {
  return num.toString().padStart(2, '0');
};

/**
 * Get next chapter ID
 * @param {string} currentId - Current chapter ID
 * @param {number} totalChapters - Total number of chapters
 * @returns {string|null} Next chapter ID or null
 */
export const getNextChapterId = (currentId, totalChapters) => {
  const num = getChapterNumber(currentId);
  if (num >= totalChapters - 1) return null;
  return formatChapterId(num + 1);
};

/**
 * Get previous chapter ID
 * @param {string} currentId - Current chapter ID
 * @returns {string|null} Previous chapter ID or null
 */
export const getPrevChapterId = (currentId) => {
  const num = getChapterNumber(currentId);
  if (num <= 0) return null;
  return formatChapterId(num - 1);
};

/* ============================================
   COLOR UTILITIES
   ============================================ */

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color code
 * @returns {Object} { r, g, b }
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

/**
 * Convert RGB to hex color
 * @param {number} r - Red value
 * @param {number} g - Green value
 * @param {number} b - Blue value
 * @returns {string} Hex color code
 */
export const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
};

/**
 * Add alpha to hex color
 * @param {string} hex - Hex color code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string
 */
export const hexToRgba = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

/* ============================================
   DEBOUNCE & THROTTLE
   ============================================ */

/**
 * Debounce a function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle a function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit time in ms
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/* ============================================
   MISC UTILITIES
   ============================================ */

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.warn('Failed to copy to clipboard:', error);
    return false;
  }
};

/**
 * Check if running in browser environment
 * @returns {boolean} Is browser
 */
export const isBrowser = () => {
  return typeof window !== 'undefined';
};

/**
 * Preload an image
 * @param {string} src - Image source URL
 * @returns {Promise} Resolves when loaded
 */
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Generate a unique ID
 * @returns {string} Unique ID
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Wait for specified milliseconds
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Resolves after wait
 */
export const wait = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
